import type { AssistantLogRecord, AssistantTurnLogRecord } from '@cumpli/shared';

/**
 * Client-side conversation search/grouping for the "Conversaciones de IA"
 * supervisory log (packages/web/src/pages/ConversacionesIA.tsx). Pure
 * functions only — no React, no network — so behavior (filter semantics,
 * grouping, and the auto-drain guard) is unit-testable under vitest's
 * default node environment.
 */

export interface Conversation {
  id: string;
  records: AssistantLogRecord[];
  lastTs: string;
  userIds: string[];
  turnCount: number;
  decisionCount: number;
  preview: string;
}

export interface AssistantLogFilters {
  q: string;
  from: string;
  to: string;
  userId: string;
  onlyDecisions: boolean;
}

/**
 * Cap on how many records the auto-drain will accumulate before it stops and
 * the UI shows the partial-coverage warning instead of fetching forever
 * (~50 requests at limit=200 — enough for a real search, bounded so a huge
 * log cannot be hammered).
 */
export const MAX_SEARCH_RECORDS = 5000;

/** True when any filter narrows the result set — this is what decides
 * whether the page owes the reviewer a complete-history search. */
export function hasActiveFilters(f: AssistantLogFilters): boolean {
  return f.q.trim() !== '' || f.from !== '' || f.to !== '' || f.userId !== '' || f.onlyDecisions;
}

/**
 * Filter + group the accumulated record set into conversations. Verbatim
 * extraction of the previous inline useMemo body in ConversacionesIA.tsx —
 * do not change any comparison, grouping key, or sort here.
 */
export function buildConversations(
  items: AssistantLogRecord[],
  f: AssistantLogFilters
): Conversation[] {
  const norm = (s: string) => s.toLowerCase();
  const needle = norm(f.q.trim());
  const fromTs = f.from ? `${f.from}T00:00:00.000Z` : '';
  const toTs = f.to ? `${f.to}T23:59:59.999Z` : '';

  const filtered = items.filter((r) => {
    if (fromTs && r.ts < fromTs) return false;
    if (toTs && r.ts > toTs) return false;
    if (f.userId && r.userId !== f.userId) return false;
    if (needle) {
      const hay = r.recordType === 'turn' ? `${norm(r.userMessage)} ${norm(r.answer)}` : norm(r.tool);
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const byConv = new Map<string, AssistantLogRecord[]>();
  for (const r of filtered) {
    const key = r.conversationId ?? 'sin-conversacion';
    byConv.set(key, [...(byConv.get(key) ?? []), r]);
  }
  let groups = [...byConv.entries()].map(([id, records]): Conversation => {
    const sorted = [...records].sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
    const turns = sorted.filter((r) => r.recordType === 'turn');
    const firstTurn = turns[0] as AssistantTurnLogRecord | undefined;
    const last = sorted[sorted.length - 1];
    return {
      id,
      records: sorted,
      lastTs: last ? last.ts : '',
      userIds: [...new Set(sorted.map((r) => r.userId))],
      turnCount: turns.length,
      decisionCount: sorted.length - turns.length,
      preview: firstTurn?.userMessage ?? '—',
    };
  });
  if (f.onlyDecisions) groups = groups.filter((g) => g.decisionCount > 0);
  groups.sort((a, b) => (a.lastTs < b.lastTs ? 1 : a.lastTs > b.lastTs ? -1 : 0));
  return groups;
}

/**
 * Whether the page should fetch another page before declaring the filtered
 * result set final. A search is only honest once it has either drained the
 * full history (nextCursor === null) or hit the hard cap — otherwise a
 * matching record on an unloaded page would be invisibly missed.
 */
export function shouldAutoLoad(
  f: AssistantLogFilters,
  nextCursor: string | null,
  loadedCount: number
): boolean {
  return hasActiveFilters(f) && nextCursor !== null && loadedCount < MAX_SEARCH_RECORDS;
}
